import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { 
  Plus, 
  DollarSign, 
  TrendingUp, 
  AlertCircle, 
  Calendar, 
  Users, 
  Settings, 
  Check, 
  Truck, 
  Info, 
  Percent, 
  Download, 
  Send, 
  RefreshCw, 
  Sliders, 
  Eye, 
  Sparkles,
  HelpCircle,
  FileText,
  FileSpreadsheet,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '../../contexts/SessionContext';
import RightSlideModal from '../../components/RightSlideModal';
import CustomerDropdown from '../../components/CustomerDropdown';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from 'recharts';

const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convert = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };
  const n = Math.floor(num);
  return n === 0 ? 'Zero' : convert(n);
};

export default function FeesPage() {
  const { selectedSession } = useSession();
  const [loading, setLoading] = useState(true);
  
  // Data States
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  
  // Active selected student for billing ledger
  const [selectedStudentId, setSelectedStudentId] = useState('');
  
  // Persistent Configurable Settings States
  const [feeComponents, setFeeComponents] = useState([]);
  const [transportRoutes, setTransportRoutes] = useState([]);
  const [discountRules, setDiscountRules] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [school, setSchool] = useState(null);
  
  // Navigation / Modal States
  const [activeTab, setActiveTab] = useState('billing'); // 'billing', 'analytics'
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  const [billingSubTab, setBillingSubTab] = useState('ledger'); // 'ledger' | 'transactions'

  // Form inputs
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash', 'card', 'bank_transfer'

  // Academic months (April to March)
  const billingMonths = [
    { key: 'apr', name: 'April 2026', num: 4 },
    { key: 'may', name: 'May 2026', num: 5 },
    { key: 'jun', name: 'June 2026', num: 6 },
    { key: 'jul', name: 'July 2026', num: 7 },
    { key: 'aug', name: 'August 2026', num: 8 },
    { key: 'sep', name: 'September 2026', num: 9 },
    { key: 'oct', name: 'October 2026', num: 10 },
    { key: 'nov', name: 'November 2026', num: 11 },
    { key: 'dec', name: 'December 2026', num: 12 },
    { key: 'jan', name: 'January 2027', num: 1 },
    { key: 'feb', name: 'February 2027', num: 2 },
    { key: 'mar', name: 'March 2027', num: 3 }
  ];

  useEffect(() => {
    fetchBaseData();
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const [compsRes, routesRes, discountsRes] = await Promise.all([
        api.get('/fee-components'),
        api.get('/transport-routes'),
        api.get('/discount-rules')
      ]);
      setFeeComponents(compsRes.data);
      setTransportRoutes(routesRes.data);
      setDiscountRules(discountsRes.data);
    } catch (error) {
      console.error('Failed to load live configurations from MongoDB');
    }
  };

  const fetchBaseData = async () => {
    try {
      const [studentsRes, classesRes, sectionsRes, paymentsRes, userRes] = await Promise.all([
        api.get('/students'),
        api.get('/classes'),
        api.get('/sections'),
        api.get('/fee-payments'),
        api.get('/auth/me').catch(() => null)
      ]);

      setClasses(classesRes.data);
      setSections(sectionsRes.data);
      if (userRes && userRes.data?.school) {
        setSchool(userRes.data.school);
      }
      
      // Let's seed demo familyIds and transport profiles to students if they are missing
      const rawStudents = studentsRes.data;
      const enhancedStudents = rawStudents.map((s, idx) => {
        // Group students into family groups dynamically
        let familyId = s.familyId || `FAM-${100 + (idx % 4)}`;
        let useTransport = s.useTransport !== undefined ? s.useTransport : (idx % 3 === 0);
        let routeId = s.routeId || (useTransport ? 'r1' : null);
        let pickupPoint = s.pickupPoint || (useTransport ? 'Royal Residency' : '');
        let siblingDiscountApplied = s.siblingDiscountApplied !== undefined ? s.siblingDiscountApplied : (idx % 4 === 1);
        let scholarshipPercent = s.scholarshipPercent !== undefined ? s.scholarshipPercent : (idx % 6 === 0 ? 20 : 0);

        return {
          ...s,
          familyId,
          useTransport,
          routeId,
          pickupPoint,
          siblingDiscountApplied,
          scholarshipPercent
        };
      });
      
      setStudents(enhancedStudents);
      setAllPayments(paymentsRes.data || []);


    } catch (error) {
      toast.error('Failed to fetch ledger base data from backend');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  LEDGER CALCULATION ENGINE  (pure, testable, single-pass)
  //
  //  Accounting model per month:
  //    grossDue     = Σ component.net  (after discounts/waivers)
  //    advanceUsed  = min(carryFromPrev, grossDue)
  //    netPayable   = grossDue − advanceUsed
  //    paid         = real cash allocated to this month (FIFO from payments pool)
  //    remainingDue = max(0, netPayable − paid)
  //    overpaid     = max(0, paid − netPayable)   → feeds next month's advance
  //    carryForward = (advance leftover after this month) + overpaid
  //    status       = remainingDue===0 ? 'paid' : paid>0 ? 'partial' : 'unpaid'
  // ─────────────────────────────────────────────────────────────
  const getStudentLedger = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return [];

    // ── Resolve fee components for this student ──────────────────
    const resolvedComponents = [];
    const componentNames = Array.from(new Set(feeComponents.map(c => c.name?.toLowerCase())));

    componentNames.forEach(nameLower => {
      const customComp = feeComponents.find(c => c.name?.toLowerCase() === nameLower && c.classId === student.classId);
      const defaultComp = feeComponents.find(c => c.name?.toLowerCase() === nameLower && c.classId === 'all');
      const activeComp = customComp || defaultComp;
      if (activeComp) resolvedComponents.push(activeComp);
    });

    // ── Cash pool: total real money received, allocated FIFO ──────
    const studentPayments = allPayments.filter(p => p.studentId === studentId);
    let cashPool = studentPayments.reduce((acc, p) => acc + p.amount, 0);

    // ── Walk months ───────────────────────────────────────────────
    let carryForward = 0; // advance balance carried into current month
    const ledger = [];

    billingMonths.forEach((month, index) => {
      // 1. Build components due this month
      const components = [];

      resolvedComponents.forEach(comp => {
        const nameLower = comp.name?.toLowerCase();
        if (nameLower.includes('transport')) return; // handled separately

        let shouldCharge = false;
        if (comp.frequency === 'monthly')    shouldCharge = true;
        else if (comp.frequency === 'quarterly') shouldCharge = [4,7,10,1].includes(month.num);
        else if (comp.frequency === 'annual')    shouldCharge = month.num === 10;
        else if (comp.frequency === 'one-time')  shouldCharge = index === 0;

        if (!shouldCharge) return;

        let discount = 0;
        if (nameLower.includes('tuition')) {
          if (student.siblingDiscountApplied) discount += comp.amount * 0.5;
          if (student.scholarshipPercent > 0) discount += comp.amount * (student.scholarshipPercent / 100);
        }
        const net = Math.max(0, comp.amount - discount);
        components.push({ name: comp.name, original: comp.amount, discount, net, frequency: comp.frequency });
      });

      // Transport component
      if (student.useTransport) {
        const route = transportRoutes.find(r => r.id === student.routeId);
        const routeFee = route ? route.monthlyFee : 1200;
        components.push({ name: 'Transport Fee', original: routeFee, discount: 0, net: routeFee, frequency: 'monthly' });
      }

      // 2. Core accounting math
      const grossDue     = components.reduce((a, c) => a + c.net, 0);
      const advanceUsed  = Math.min(carryForward, grossDue);
      const netPayable   = grossDue - advanceUsed;

      // Allocate real cash from pool to cover netPayable
      const cashForMonth = Math.min(cashPool, netPayable);
      cashPool          -= cashForMonth;

      const paid         = cashForMonth;
      const remainingDue = Math.max(0, netPayable - paid);
      const overpaid     = Math.max(0, paid - netPayable); // cash surplus this month

      // Advance left after this month + any overpayment from cash = next month's carry
      const advanceLeftover = carryForward - advanceUsed;
      carryForward          = advanceLeftover + overpaid;

      // If this is the last month, any remaining cashPool becomes advance
      if (index === billingMonths.length - 1 && cashPool > 0) {
        carryForward += cashPool;
        cashPool = 0;
      }

      const status =
        remainingDue === 0 ? 'paid' :
        paid > 0           ? 'partial' :
                             'unpaid';

      ledger.push({
        monthKey:     month.key,
        monthName:    month.name,
        components,
        grossDue,
        advanceUsed,
        netPayable,
        paid,
        remainingDue,
        carryForward,
        // legacy alias so receipt modal still works
        totalDue:             grossDue,
        outstanding:          remainingDue,
        advanceCarryForward:  carryForward,
        status,
      });
    });

    return ledger;
  };


  const getActiveStudent = () => {
    return students.find(s => s.id === selectedStudentId);
  };

  const activeLedger = getStudentLedger(selectedStudentId);
  const activeStudent = getActiveStudent();

  // Metrics and Analytics calculations for current tab
  const getOverallMetrics = () => {
    let totalExpected = 0;
    let totalCollected = 0;
    let totalDefaulters = 0;
    let totalTransportRevenue = 0;
    let totalDiscountsGiven = 0;

    students.forEach(s => {
      const ledger = getStudentLedger(s.id);
      ledger.forEach(l => {
        totalExpected += l.totalDue;
        totalCollected += l.paid;
        
        l.components.forEach(c => {
          totalDiscountsGiven += c.discount;
        });

        const transportComp = l.components.find(c => c.name === 'Transport Fee');
        if (transportComp) {
          totalTransportRevenue += transportComp.net;
        }
      });

      const hasDues = ledger.some(l => l.outstanding > 0);
      if (hasDues) totalDefaulters++;
    });

    return {
      totalExpected,
      totalCollected,
      totalPending: Math.max(0, totalExpected - totalCollected),
      totalDefaulters,
      totalTransportRevenue,
      totalDiscountsGiven
    };
  };

  const overallMetrics = getOverallMetrics();

  // Record a payment and write directly to MongoDB
  const handleRecordPaymentSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      const response = await api.post('/fee-payments', {
        studentId: selectedStudentId,
        amount,
        quarter: 'Monthly Allocation',
        paymentDate: new Date(paymentDate).toISOString(),
        remarks: paymentRemarks || 'Ledger payment recorded successfully',
        status: 'paid'
      });

      const newPayment = response.data;
      setAllPayments(prev => [newPayment, ...prev]);
      toast.success(`Allocated ₹${amount.toLocaleString()} into student ledger on MongoDB!`);
      
      // Auto-display allocation receipt
      setSelectedReceipt({
        student: activeStudent,
        payment: newPayment,
        ledgerState: activeLedger
      });
      
      setPaymentAmount('');
      setPaymentRemarks('');
      setShowPaymentModal(false);
      setShowReceiptModal(true);
    } catch (error) {
      toast.error('Failed to save payment transaction to MongoDB');
    }
  };

  // Toggle student transport settings and update MongoDB
  const handleToggleTransport = async (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return;

    const useTransport = !student.useTransport;
    const defaultRoute = transportRoutes.length > 0 ? transportRoutes[0] : null;
    const routeId = useTransport ? (defaultRoute?.id || 'r1') : null;
    const pickupPoint = useTransport ? (defaultRoute?.pickupPoints?.[0] || 'Default Stop') : '';

    try {
      await api.put(`/students/${studentId}/billing`, {
        useTransport,
        routeId,
        pickupPoint
      });

      setStudents(prev => prev.map(s => {
        if (s.id === studentId) {
          return { ...s, useTransport, routeId, pickupPoint };
        }
        return s;
      }));

      toast.success(`Transport toggled ${useTransport ? 'ON' : 'OFF'} for ${student.name} on backend!`);
    } catch (error) {
      toast.error('Failed to save transport profile configuration');
    }
  };

  // Export student ledger as CSV
  const handleExportCSV = () => {
    if (!activeStudent) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Month,Expected Dues (₹),Paid Dues (₹),Outstanding Balance (₹),Advance Carry (₹),Status\n";
    
    activeLedger.forEach(l => {
      csvContent += `"${l.monthName}",${l.totalDue},${l.paid},${l.outstanding},${l.advanceCarryForward},"${l.status.toUpperCase()}"\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${activeStudent.name.replace(/\s+/g, '_')}_billing_ledger.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Billing ledger CSV exported successfully!');
  };

  const handleSendReminder = (student) => {
    const studentLedger = getStudentLedger(student.id);
    const totalOutstanding = studentLedger.reduce((acc, curr) => acc + curr.outstanding, 0);

    if (totalOutstanding === 0) {
      toast.info('Student has no outstanding dues to remind');
      return;
    }

    toast.success(`WhatsApp reminder template queued for ${student.name}'s parents. Total Dues: ₹${totalOutstanding.toLocaleString()}`);
  };

  return (
    <div className="space-y-6">
      
      {/* Stripe-style Top header layout with analytics toggle */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-2 border-b border-slate-200/50">
        <div>
          <h2 className="font-geom font-bold text-3xl text-slate-800 tracking-tight">Fees & Dues</h2>
          <p className="text-sm text-slate-500 mt-1">Smart monthly ledger allocation engine and real-time sibling waiver discounts</p>
        </div>
        <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200/40 shrink-0">
          <button 
            onClick={() => setActiveTab('billing')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeTab === 'billing' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Sliders className="w-4 h-4" />
            Student Ledger
          </button>
          <button 
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${activeTab === 'analytics' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <TrendingUp className="w-4 h-4" />
            Analytics
          </button>
        </div>
      </div>

      {activeTab === 'billing' && (
        <div className="space-y-6 animate-in fade-in">
          
          {/* Top segment: Select Student & Sibling card side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* SEARCHABLE PREMIUM CUSTOMER DROPDOWN BOX */}
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Select Student</h3>
              
              <CustomerDropdown
                options={students}
                selectedValue={selectedStudentId}
                onChange={setSelectedStudentId}
                classes={classes}
                sections={sections}
                placeholder="Search pupil..."
              />

              {activeStudent && (
                <div className="pt-4 border-t border-slate-100 space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">Family ID Grouping</span>
                    <span className="font-mono bg-slate-50 border border-slate-200/60 px-2 py-0.5 rounded text-slate-600 font-bold select-all">
                      {activeStudent.familyId}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">Sibling Concession (50%)</span>
                    <span className={`px-2 py-0.5 rounded font-bold uppercase text-[9px] ${activeStudent.siblingDiscountApplied ? 'bg-green-50 text-green-700 border border-green-200/40' : 'bg-slate-100 text-slate-400'}`}>
                      {activeStudent.siblingDiscountApplied ? 'AUTO APPLIED' : 'INACTIVE'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">Academic Scholarship</span>
                    <span className={`px-2 py-0.5 rounded font-bold uppercase text-[9px] ${activeStudent.scholarshipPercent > 0 ? 'bg-indigo-50 text-indigo-700 border border-indigo-200/40' : 'bg-slate-100 text-slate-400'}`}>
                      {activeStudent.scholarshipPercent > 0 ? `${activeStudent.scholarshipPercent}% WAIVER` : 'NONE'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 font-medium">Transport Active Route</span>
                    <span className="font-semibold text-slate-700">
                      {activeStudent.useTransport ? (transportRoutes.find(r => r.id === activeStudent.routeId)?.name || 'Active') : 'Disabled'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Sibling list group */}
            {activeStudent ? (
              <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Family Siblings</h3>
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-bold uppercase">
                      {students.filter(s => s.familyId === activeStudent.familyId).length} Members
                    </span>
                  </div>
                  <div className="space-y-3 max-h-[160px] overflow-y-auto pr-1">
                    {students.filter(s => s.familyId === activeStudent.familyId).map(sibling => (
                      <div key={sibling.id} className={`p-3 rounded-xl border flex items-center justify-between ${sibling.id === activeStudent.id ? 'bg-indigo-50/40 border-indigo-100' : 'bg-slate-50/50 border-slate-100'}`}>
                        <div>
                          <p className="text-sm font-bold text-slate-800">{sibling.name}</p>
                          <p className="text-xs text-slate-400">Roll: {sibling.rollNumber} • {sibling.siblingDiscountApplied ? 'Younger Child (Discounted)' : 'First Child'}</p>
                        </div>
                        {sibling.siblingDiscountApplied && (
                          <Percent className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* AI Insights / Synchronized state */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
                  Real-time sibling concession rules synced on MongoDB
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm flex flex-col items-center justify-center text-center text-slate-400">
                <Users className="w-10 h-10 text-slate-300 mb-2" />
                <h3 className="text-xs font-bold uppercase tracking-wider">No Student Selected</h3>
                <p className="text-[11px] text-slate-400 mt-1 max-w-[240px]">
                  Select a student from the dropdown box to load their family siblings and concessions.
                </p>
              </div>
            )}
          </div>

          {/* Sub-tab switcher */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
            <button
              onClick={() => setBillingSubTab('ledger')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${billingSubTab === 'ledger' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Monthly Ledger
            </button>
            <button
              onClick={() => setBillingSubTab('transactions')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${billingSubTab === 'transactions' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Transactions
            </button>
          </div>

          {/* Monthly ledger board full width below */}
          <div className="w-full space-y-6">
            {activeStudent ? (
              <>
                {/* ── LEDGER TAB ── */}
                {billingSubTab === 'ledger' && (
                  <>
                    {/* Header / billing control buttons */}
                    <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h3 className="font-geom font-bold text-xl text-slate-800">
                            {activeStudent?.name}'s Financial Ledger
                          </h3>
                          <p className="text-xs text-slate-400">Monthly billing allocation & advances for session {selectedSession}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleExportCSV}
                            className="p-2.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition-all"
                            title="Export CSV"
                          >
                            <FileSpreadsheet className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setShowPaymentModal(true)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5 py-2.5 text-xs font-bold shadow-sm transition-all flex items-center gap-2"
                          >
                            <Plus className="w-4 h-4" />
                            Record Ledger Payment
                          </button>
                        </div>
                      </div>

                      {/* ── Financial summary cards ── */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
                        <div className="p-3 rounded-xl border" style={{ background: 'linear-gradient(135deg,rgba(240,253,244,0.9),rgba(220,252,231,0.7))', borderColor: 'rgba(134,239,172,0.4)' }}>
                          <p className="text-[9px] font-bold text-green-600 uppercase tracking-wider">Total Paid</p>
                          <p className="text-lg font-geom font-bold text-green-700 mt-0.5">
                            ₹{activeLedger.reduce((a, r) => a + r.paid, 0).toLocaleString()}
                          </p>
                          <p className="text-[9px] text-green-500 mt-0.5">{activeLedger.filter(r => r.status === 'paid').length} months cleared</p>
                        </div>
                        <div className="p-3 rounded-xl border" style={{ background: 'linear-gradient(135deg,rgba(255,241,242,0.9),rgba(254,226,226,0.7))', borderColor: 'rgba(252,165,165,0.4)' }}>
                          <p className="text-[9px] font-bold text-rose-600 uppercase tracking-wider">Outstanding</p>
                          <p className="text-lg font-geom font-bold text-rose-700 mt-0.5">
                            ₹{activeLedger.reduce((a, r) => a + r.remainingDue, 0).toLocaleString()}
                          </p>
                          <p className="text-[9px] text-rose-400 mt-0.5">{activeLedger.filter(r => r.status !== 'paid').length} months pending</p>
                        </div>
                        <div className="p-3 rounded-xl border" style={{ background: 'linear-gradient(135deg,rgba(238,242,255,0.9),rgba(224,231,255,0.7))', borderColor: 'rgba(165,180,252,0.4)' }}>
                          <p className="text-[9px] font-bold text-indigo-600 uppercase tracking-wider">Advance Balance</p>
                          <p className="text-lg font-geom font-bold text-indigo-700 mt-0.5">
                            ₹{(activeLedger[activeLedger.length - 1]?.carryForward || 0).toLocaleString()}
                          </p>
                          <p className="text-[9px] text-indigo-400 mt-0.5">Carry-forward credit</p>
                        </div>
                        <div className="p-3 rounded-xl border bg-slate-50/80" style={{ borderColor: 'rgba(148,163,184,0.3)' }}>
                          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Gross Billed</p>
                          <p className="text-lg font-geom font-bold text-slate-700 mt-0.5">
                            ₹{activeLedger.reduce((a, r) => a + r.grossDue, 0).toLocaleString()}
                          </p>
                          <p className="text-[9px] text-slate-400 mt-0.5">Before advance adjustments</p>
                        </div>
                      </div>
                    </div>

                    {/* ── Main Month-wise Ledger Table ── */}
                    <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr style={{ background: 'linear-gradient(135deg,rgba(248,250,252,0.95),rgba(238,242,255,0.80))' }} className="border-b border-slate-200/40">
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest w-8"></th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Month</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Fee Components</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Gross Due</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-500 uppercase tracking-widest text-right" title="To Pay = Gross Due − Advance Used">To Pay ⓘ</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-green-500 uppercase tracking-widest text-right">Paid</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-rose-400 uppercase tracking-widest text-right">Remaining</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                              <th className="px-4 py-3.5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Receipt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeLedger.map((month) => {
                              const isExpanded = !!expandedRows[month.monthKey];
                              const rowBg =
                                month.status === 'paid'    ? 'rgba(240,253,244,0.45)' :
                                month.status === 'partial' ? 'rgba(255,251,235,0.50)' :
                                                            'rgba(255,241,242,0.30)';
                              return (
                                <React.Fragment key={month.monthKey}>
                                  <tr
                                    className="border-b border-slate-100/70 transition-colors cursor-pointer"
                                    style={{ background: isExpanded ? rowBg : undefined }}
                                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = rowBg; }}
                                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = ''; }}
                                  >
                                    <td className="px-4 py-3.5 text-center">
                                      <button
                                        onClick={() => toggleRow(month.monthKey)}
                                        className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                                      >
                                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                      </button>
                                    </td>
                                    <td className="px-4 py-3.5">
                                      <p className="font-bold text-slate-800 text-sm whitespace-nowrap">{month.monthName}</p>
                                      {month.advanceUsed > 0 && (
                                        <span className="text-[9px] text-indigo-600 font-bold bg-indigo-50 border border-indigo-100/50 px-1.5 py-0.5 rounded-full mt-1 inline-block">
                                          ↑ ₹{month.advanceUsed.toLocaleString()} advance
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3.5">
                                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                                        {month.components.map((comp, idx) => (
                                          <span
                                            key={idx}
                                            className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-semibold rounded-full border border-slate-200/50"
                                            title={comp.discount > 0 ? `Gross ₹${comp.original} − Discount ₹${comp.discount}` : undefined}
                                          >
                                            {comp.name.replace(' Fee', '')} ₹{comp.net.toLocaleString()}
                                            {comp.discount > 0 && <span className="text-green-600 ml-0.5">✦</span>}
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3.5 text-right">
                                      <span className="font-bold text-slate-700 text-sm font-mono">₹{month.grossDue.toLocaleString()}</span>
                                    </td>
                                    <td className="px-4 py-3.5 text-right">
                                      <span className="font-black text-slate-800 text-sm font-mono">₹{month.netPayable.toLocaleString()}</span>
                                    </td>
                                    <td className="px-4 py-3.5 text-right">
                                      <span className={`font-semibold text-sm font-mono ${month.paid > 0 ? 'text-green-600' : 'text-slate-300'}`}>
                                        {month.paid > 0 ? `₹${month.paid.toLocaleString()}` : '—'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3.5 text-right">
                                      <span className={`font-bold text-sm font-mono ${month.remainingDue > 0 ? 'text-rose-600' : 'text-slate-300'}`}>
                                        {month.remainingDue > 0 ? `₹${month.remainingDue.toLocaleString()}` : '—'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3.5 text-center">
                                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wide border ${
                                        month.status === 'paid' ? 'bg-green-50 text-green-700 border-green-200/50' :
                                        month.status === 'partial' ? 'bg-amber-50 text-amber-700 border-amber-200/50' :
                                        'bg-rose-50 text-rose-600 border-rose-200/50'
                                      }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          month.status === 'paid' ? 'bg-green-500' :
                                          month.status === 'partial' ? 'bg-amber-500 animate-pulse' : 'bg-rose-400'
                                        }`} />
                                        {month.status === 'unpaid' ? 'Due' : month.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3.5 text-right">
                                      {month.status === 'paid' ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedReceipt({
                                              student: activeStudent,
                                              payment: {
                                                id: `rec-${month.monthKey}-${Date.now().toString().slice(-4)}`,
                                                amount: month.paid,
                                                paymentDate: new Date().toISOString(),
                                                method: 'Ledger Allocation'
                                              },
                                              monthName: month.monthName,
                                              monthComponents: month.components,
                                              totalDue: month.grossDue,
                                              paid: month.paid,
                                              status: month.status,
                                              advanceCarry: month.carryForward,
                                              advanceUsed: month.advanceUsed
                                            });
                                            setShowReceiptModal(true);
                                          }}
                                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 text-[9px] font-black rounded-xl transition-all border border-green-200/50 shadow-sm active:scale-95 whitespace-nowrap"
                                        >
                                          <Download className="w-3 h-3" />
                                          Receipt
                                        </button>
                                      ) : (
                                        <span
                                          title={month.status === 'partial' ? 'Clear remaining balance to unlock receipt' : 'No payment made yet'}
                                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-50 text-slate-300 text-[9px] font-black rounded-xl border border-slate-100 cursor-not-allowed select-none whitespace-nowrap"
                                        >
                                          <Download className="w-3 h-3" />
                                          {month.status === 'partial' ? 'Partial' : 'Due'}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr className="border-b border-indigo-100/30">
                                      <td colSpan={9} className="px-6 py-0">
                                        <div
                                          className="mb-3 mt-1 rounded-xl p-4"
                                          style={{ background: 'linear-gradient(135deg,rgba(238,242,255,0.70),rgba(224,231,255,0.50))', border: '1px solid rgba(199,210,254,0.50)' }}
                                        >
                                          <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-2">📊 Calculation Breakdown — {month.monthName}</p>
                                          <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
                                            <span className="text-slate-600">Gross Due <strong className="text-slate-800">₹{month.grossDue.toLocaleString()}</strong></span>
                                            {month.advanceUsed > 0 && (
                                              <><span className="text-slate-400">−</span>
                                              <span className="text-indigo-600">Advance Used <strong>₹{month.advanceUsed.toLocaleString()}</strong></span></>
                                            )}
                                            <span className="text-slate-400">=</span>
                                            <span className="text-slate-700">To Pay <strong className="text-slate-900">₹{month.netPayable.toLocaleString()}</strong></span>
                                            <span className="text-slate-400">−</span>
                                            <span className="text-green-600">Paid <strong>₹{month.paid.toLocaleString()}</strong></span>
                                            <span className="text-slate-400">=</span>
                                            <span className={month.remainingDue > 0 ? 'text-rose-600' : 'text-green-600'}>
                                              Remaining <strong>₹{month.remainingDue.toLocaleString()}</strong>
                                            </span>
                                          </div>
                                          {month.carryForward > 0 && (
                                            <p className="mt-2 text-[9px] text-indigo-500 font-semibold">
                                              ↪ ₹{month.carryForward.toLocaleString()} advance will carry forward to the next month
                                            </p>
                                          )}
                                          <div className="mt-3 flex flex-wrap gap-2">
                                            {month.components.map((c, i) => (
                                              <div key={i} className="bg-white/70 rounded-lg px-3 py-1.5 border border-indigo-100/50 text-[10px]">
                                                <span className="font-bold text-slate-700">{c.name}</span>
                                                {c.discount > 0 && <span className="text-slate-400 ml-1">₹{c.original} − ₹{c.discount} discount</span>}
                                                <span className="ml-1 font-black text-indigo-700">=₹{c.net}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Transport toggle card */}
                    <div className="p-4 bg-slate-50 border border-slate-200/50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                          <Truck className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-800">Transport Fee Configuration</h4>
                          <p className="text-xs text-slate-400">Route, Pickup point allocation fee per month</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleTransport(selectedStudentId)}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
                          activeStudent?.useTransport
                            ? 'bg-rose-50 text-rose-700 border-rose-200/50 hover:bg-rose-100'
                            : 'bg-indigo-600 text-white border-transparent hover:bg-indigo-700'
                        }`}
                      >
                        {activeStudent?.useTransport ? 'Disable Transport Fee' : 'Activate Transport Fee'}
                      </button>
                    </div>
                  </>
                )}

                {/* ── TRANSACTIONS TAB ── */}
                {billingSubTab === 'transactions' && (() => {
                  const studentTxns = [...allPayments]
                    .filter(p => p.studentId === selectedStudentId)
                    .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
                  return (
                    <div className="bg-white rounded-2xl border border-slate-200/40 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <h3 className="font-geom font-bold text-base text-slate-800">Payment Transactions</h3>
                          <p className="text-xs text-slate-400 mt-0.5">{studentTxns.length} records for {activeStudent?.name}</p>
                        </div>
                        <span className="text-[10px] font-bold bg-green-50 text-green-700 border border-green-200/40 px-2.5 py-1 rounded-full uppercase tracking-wide">
                          Total ₹{studentTxns.reduce((a, p) => a + p.amount, 0).toLocaleString()}
                        </span>
                      </div>

                      {studentTxns.length === 0 ? (
                        <div className="py-16 text-center">
                          <Calendar className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                          <p className="text-sm font-semibold text-slate-400">No transactions recorded yet</p>
                          <p className="text-xs text-slate-300 mt-1">Record a payment to see it here</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100/70">
                          {studentTxns.map((txn, idx) => {
                            const d = new Date(txn.paymentDate);
                            const dateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                            const timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
                            return (
                              <div key={txn.id || idx} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50/60 transition-colors">
                                <div className="flex items-center gap-4">
                                  <div className="w-9 h-9 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
                                    <DollarSign className="w-4 h-4 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold text-slate-800">₹{txn.amount.toLocaleString()}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">{txn.remarks || 'Ledger payment'}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs font-semibold text-slate-700">{dateStr}</p>
                                  <p className="text-[11px] text-slate-400 mt-0.5">{timeStr}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200/40 p-12 text-center shadow-sm flex flex-col items-center justify-center min-h-[300px]">
                <Users className="w-12 h-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-700 font-geom mb-2">No Student Selected</h3>
                <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
                  Use the premium customer dropdown search box above to locate and select a pupil to load their monthly financial billing ledger.
                </p>
              </div>
            )}
          </div>

        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6 animate-in fade-in">
          
          {/* Main Top Metrics dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Expected</p>
              <p className="text-2xl font-geom font-bold text-slate-800 mt-2">₹{overallMetrics.totalExpected.toLocaleString()}</p>
              <span className="text-[10px] text-slate-400 block mt-1">Based on global curriculum</span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Net Collected</p>
              <p className="text-2xl font-geom font-bold text-green-600 mt-2">₹{overallMetrics.totalCollected.toLocaleString()}</p>
              <span className="text-[10px] text-green-500 font-semibold block mt-1">
                {Math.round((overallMetrics.totalCollected / (overallMetrics.totalExpected || 1)) * 100)}% Conversion rate
              </span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Welfare & Sibling Discounts</p>
              <p className="text-2xl font-geom font-bold text-indigo-600 mt-2">₹{overallMetrics.totalDiscountsGiven.toLocaleString()}</p>
              <span className="text-[10px] text-indigo-500 font-semibold block mt-1">Auto-applied sibling waivers</span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Active Defaulters</p>
              <p className="text-2xl font-geom font-bold text-rose-600 mt-2">{overallMetrics.totalDefaulters} Pupils</p>
              <span className="text-[10px] text-slate-400 block mt-1">Outstanding dues present in FIFO</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Monthly Trend Area Chart */}
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm">
              <h3 className="font-geom font-bold text-base text-slate-800 mb-4">Monthly Dues Collections Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={[
                      { month: 'Apr', Dues: 75000, Collected: 71000 },
                      { month: 'May', Dues: 75000, Collected: 68000 },
                      { month: 'Jun', Dues: 82000, Collected: 64000 },
                      { month: 'Jul', Dues: 78000, Collected: 55000 },
                      { month: 'Aug', Dues: 78000, Collected: 42000 },
                      { month: 'Sep', Dues: 78000, Collected: 31000 }
                    ]}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="Collected" stroke="#4f46e5" strokeWidth={2} fillOpacity={1} fill="url(#colorCollected)" />
                    <Area type="monotone" dataKey="Dues" stroke="#cbd5e1" strokeWidth={1} fillOpacity={0} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Component Wise Breakdown Chart */}
            <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm">
              <h3 className="font-geom font-bold text-base text-slate-800 mb-4">Fee Category Revenue Distributions</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { category: 'Tuition', amount: 84000, fill: '#4f46e5' },
                      { category: 'Transport', amount: 15600, fill: '#10b981' },
                      { category: 'Hostel', amount: 12000, fill: '#06b6d4' },
                      { category: 'Computer', amount: 6400, fill: '#f59e0b' },
                      { category: 'Exam', amount: 4800, fill: '#ec4899' }
                    ]}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <XAxis dataKey="category" stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <Tooltip />
                    <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                      {[0, 1, 2, 3, 4].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#4f46e5', '#10b981', '#06b6d4', '#f59e0b', '#ec4899'][index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>
        </div>
      )}

      {activeTab === 'configurations' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in">
          
          {/* Fee Components management */}
          <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-geom font-bold text-base text-slate-800">Fee Components</h3>
              <button 
                onClick={() => { setConfigModalType('component'); setShowConfigModal(true); }}
                className="p-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors border border-slate-200/40"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {feeComponents.map(comp => (
                <div key={comp.id} className="p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-slate-700">{comp.name}</h4>
                    <p className="text-xs text-slate-400 capitalize">
                      {comp.frequency} • {comp.type} • {comp.classId === 'all' ? 'All Classes' : `Class: ${classes.find(c => c.id === comp.classId)?.className || 'Overridden'}`}
                    </p>
                  </div>
                  <span className="text-sm font-geom font-bold text-slate-800">₹{comp.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Transport Routes management */}
          <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-geom font-bold text-base text-slate-800">Transport Routes</h3>
              <button 
                onClick={() => { setConfigModalType('transport'); setShowConfigModal(true); }}
                className="p-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors border border-slate-200/40"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {transportRoutes.map(route => (
                <div key={route.id} className="p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-slate-700">{route.name}</h4>
                      <p className="text-xs text-slate-400">Code: {route.routeCode}</p>
                    </div>
                    <span className="text-sm font-geom font-bold text-indigo-600">₹{route.monthlyFee}/mo</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {route.pickupPoints.map((point, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px] font-medium">
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sibling discounts Rule Configurator Engine */}
          <div className="bg-white rounded-2xl border border-slate-200/40 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-geom font-bold text-base text-slate-800">Discount Rules Engine</h3>
              <button 
                onClick={() => { setConfigModalType('discount'); setShowConfigModal(true); }}
                className="p-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors border border-slate-200/40"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {discountRules.map(rule => (
                <div key={rule.id} className="p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 space-y-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-slate-700">{rule.name}</h4>
                    <span className="text-xs font-geom font-bold bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-200/30">
                      {rule.valueType === 'percentage' ? `${rule.value}% OFF` : `₹${rule.value} OFF`}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    <span className="font-semibold text-slate-500">Condition:</span> {rule.condition}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* Record Payment Ledger Modal */}
      <RightSlideModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Record Smart Payment Allocation"
        maxWidth="max-w-xl"
      >
        <form onSubmit={handleRecordPaymentSubmit} className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
            <h4 className="text-xs font-bold text-slate-400 uppercase">Selected Pupil</h4>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-800">{activeStudent?.name}</span>
              <span className="text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full font-semibold">
                Roll: {activeStudent?.rollNumber}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm font-semibold"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm font-semibold text-slate-700"
              >
                <option value="cash">Cash Payment</option>
                <option value="card">Card Terminal</option>
                <option value="bank_transfer">Direct Bank Transfer</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Total Amount Received (₹)</label>
            <input
              type="number"
              placeholder="e.g. 7000"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-base font-bold text-slate-800"
              required
            />
          </div>

          {/* REAL-TIME PAYMENT ALLOCATION VISUALIZER */}
          {paymentAmount && parseFloat(paymentAmount) > 0 && (
            <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/50 space-y-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <Info className="w-4 h-4" />
                <h4 className="text-xs font-bold uppercase tracking-wider">Live Allocation Plan</h4>
              </div>
              <div className="space-y-2 text-xs text-indigo-950 font-medium">
                {(() => {
                  const amt = parseFloat(paymentAmount);
                  const dues = activeLedger.reduce((acc, curr) => acc + curr.outstanding, 0);
                  if (amt > dues) {
                    return (
                      <>
                        <p className="flex justify-between">
                          <span>Clears total outstanding dues:</span>
                          <span className="font-bold text-indigo-700">₹{dues.toLocaleString()}</span>
                        </p>
                        <p className="flex justify-between border-t border-indigo-100/50 pt-1.5 mt-1.5">
                          <span className="font-bold text-green-700">Excess rolls forward as advance balance:</span>
                          <span className="font-bold text-green-700">₹{(amt - dues).toLocaleString()}</span>
                        </p>
                      </>
                    );
                  } else {
                    return (
                      <p className="flex justify-between">
                        <span>Partially clears oldest dues:</span>
                        <span className="font-bold text-amber-700">₹{amt.toLocaleString()}</span>
                      </p>
                    );
                  }
                })()}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Remarks / Audit Note</label>
            <textarea
              rows="2"
              value={paymentRemarks}
              onChange={(e) => setPaymentRemarks(e.target.value)}
              placeholder="e.g. Full year billing clearance or Q1 advance"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none text-sm"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button 
              type="submit"
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold shadow-sm transition-all"
            >
              Allocate Payment
            </button>
            <button 
              type="button" 
              onClick={() => setShowPaymentModal(false)}
              className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-full py-3 font-medium transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </RightSlideModal>

      {/* Classic School Fee Receipt Modal */}
      {selectedReceipt && (() => {
        const rcpt = selectedReceipt;
        const student = rcpt.student;
        const studentClass = classes.find(c => c.id === student?.classId);
        const studentSection = sections.find(s => s.id === student?.sectionId);
        const totalDue = rcpt.totalDue ?? rcpt.payment?.amount ?? 0;
        const paidAmt = rcpt.paid ?? rcpt.payment?.amount ?? 0;
        const balanceDue = Math.max(0, totalDue - paidAmt);
        const receiptNum = rcpt.payment?.id?.replace(/\D/g, '').slice(-4) || '0001';
        const rawDate = rcpt.payment?.paymentDate ? new Date(rcpt.payment.paymentDate) : new Date();
        const receiptDate = rawDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
        const amountWords = numberToWords(paidAmt);

        const ReceiptCopy = ({ label }) => (
          <div className="flex-1 min-w-0" style={{ fontFamily: 'Georgia, serif', border: '3px solid #1a1a1a' }}>
            {/* School header */}
            <div className="text-center px-3 pt-3 pb-2" style={{ borderBottom: '2px solid #1a1a1a' }}>
              {school?.logoUrl && (
                <img src={school.logoUrl} alt="logo" className="w-10 h-10 object-contain mx-auto mb-1" />
              )}
              <div className="font-bold text-sm leading-tight uppercase">{school?.name || 'School Name'}</div>
              <div className="text-xs leading-tight mt-0.5">{school?.address || 'School Address'}</div>
              {school?.phone && <div className="text-xs">Ph.: {school.phone}</div>}
              <div className="font-bold text-sm mt-2 underline tracking-widest">FEES RECEIPT</div>
            </div>

            {/* Receipt metadata */}
            <div className="px-3 py-2 text-xs space-y-1" style={{ borderBottom: '2px solid #1a1a1a' }}>
              <div className="flex justify-between">
                <span>Receipt No. <strong>{receiptNum}</strong></span>
                <span>Date : <strong>{receiptDate}</strong></span>
              </div>
              <div>Regn. No. : <strong>{student?.admissionNumber || student?.rollNumber || '—'}</strong></div>
              <div>Student name : <strong>{student?.name || '—'}</strong></div>
              <div>Father's Name : <strong>{student?.parentName || '—'}</strong></div>
              <div className="flex justify-between">
                <span>Class / Standard : <strong>{studentClass?.className || student?.classId || '—'}</strong></span>
                <span>Section : <strong>{studentSection?.sectionName || student?.sectionId || '—'}</strong></span>
              </div>
            </div>

            {/* Fee items table */}
            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#c8c8c8' }}>
                  <th className="text-left px-3 py-1.5 font-bold" style={{ borderBottom: '2px solid #1a1a1a', borderRight: '1px solid #1a1a1a' }}>
                    Student's Fees Details
                  </th>
                  <th className="text-right px-3 py-1.5 font-bold w-24" style={{ borderBottom: '2px solid #1a1a1a' }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {rcpt.monthComponents ? (
                  rcpt.monthComponents.map((comp, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-1.5" style={{ borderBottom: '1px solid #ddd', borderRight: '1px solid #1a1a1a' }}>{comp.name}</td>
                      <td className="px-3 py-1.5 text-right font-bold" style={{ borderBottom: '1px solid #ddd' }}>{comp.net.toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-3 py-1.5" style={{ borderBottom: '1px solid #ddd', borderRight: '1px solid #1a1a1a' }}>Fee Payment</td>
                    <td className="px-3 py-1.5 text-right font-bold" style={{ borderBottom: '1px solid #ddd' }}>{paidAmt.toLocaleString()}</td>
                  </tr>
                )}
                {/* Spacer row */}
                <tr>
                  <td className="px-3 py-3" style={{ borderBottom: '1px solid #1a1a1a', borderRight: '1px solid #1a1a1a' }}>&nbsp;</td>
                  <td className="px-3 py-3" style={{ borderBottom: '1px solid #1a1a1a' }}>&nbsp;</td>
                </tr>
                {/* Summary rows */}
                <tr>
                  <td className="px-3 py-1.5 font-semibold" style={{ borderBottom: '1px solid #ddd', borderRight: '1px solid #1a1a1a' }}>Total Fee</td>
                  <td className="px-3 py-1.5 text-right font-bold" style={{ borderBottom: '1px solid #ddd' }}>{totalDue.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="px-3 py-1.5 font-semibold" style={{ borderBottom: '1px solid #ddd', borderRight: '1px solid #1a1a1a' }}>Paid Fee</td>
                  <td className="px-3 py-1.5 text-right font-bold" style={{ borderBottom: '1px solid #ddd' }}>{paidAmt.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="px-3 py-1.5 font-semibold" style={{ borderRight: '1px solid #1a1a1a' }}>Balance Fee</td>
                  <td className="px-3 py-1.5 text-right font-bold">{balanceDue.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>

            {/* Amount in words */}
            <div className="px-3 py-2 text-xs font-semibold" style={{ borderTop: '2px solid #1a1a1a' }}>
              Rupees {amountWords} Only
            </div>

            {/* Copy label */}
            <div className="text-right text-[10px] text-gray-500 px-2 pb-1" style={{ borderTop: '1px solid #ddd' }}>
              {label}
            </div>
          </div>
        );

        return (
          <RightSlideModal
            isOpen={showReceiptModal}
            onClose={() => setShowReceiptModal(false)}
            title="Fee Receipt"
            maxWidth="max-w-3xl"
          >
            <style dangerouslySetInnerHTML={{__html: `
              @media print {
                body * { visibility: hidden !important; }
                #fee-receipt-print, #fee-receipt-print * { visibility: visible !important; }
                #fee-receipt-print {
                  position: absolute !important;
                  left: 0 !important; top: 0 !important;
                  width: 100% !important;
                  padding: 12px !important;
                  background: white !important;
                }
              }
            `}} />

            <div id="fee-receipt-print" className="bg-white">
              <ReceiptCopy label="Original" />
            </div>

            <div className="flex gap-2 mt-4 print:hidden">
              <button
                onClick={() => window.print()}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-full py-3 font-bold text-xs uppercase tracking-wide flex items-center justify-center gap-2 shadow-sm transition-all"
              >
                <Download className="w-4 h-4" />
                Print / Download PDF
              </button>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-full py-3 font-bold text-xs uppercase tracking-wide transition-all"
              >
                Close Receipt
              </button>
            </div>
          </RightSlideModal>
        );
      })()}

    </div>
  );
}
